import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {InvoicesService} from '../../view-invoices/invoices.service';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {InvoiceSummaryViewModel} from '../../view-invoices/view-invoice-summaries/view-models/invoice-summary-view.model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {InvoiceSummaryConverterService} from '../../view-invoices/view-invoice-summaries/view-models/invoice-summary-converter.service';
import {AppParameterMap} from '../../config/app-parameter-map';
import {PARAM_ENTITY_ID} from '../../config/app-urls';
import {Router} from '@angular/router';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {InvoiceCardContainerComponent} from '../../view-invoices/view-invoice-summaries/invoice-card-container/invoice-card-container.component';
import {ViewInvoiceDetailsComponent} from '../../view-invoices/view-invoice-details/view-invoice-details.component';

@Component({
    selector: 'app-client-invoice',
    templateUrl: './client-invoice.component.html',
    styleUrls: ['./client-invoice.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, InvoiceCardContainerComponent, ViewInvoiceDetailsComponent]
})
export class ClientInvoiceComponent implements OnInit, TableTemplateComponentHost {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly converter = inject(InvoiceSummaryConverterService);
  private readonly clientsideDataService = inject(ClientsideDataService<InvoiceSummaryViewModel>);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly entityId = signal('');
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('totalRemainingAmount');
  readonly sortOrder = signal(-1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<InvoiceSummaryViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  readonly clientInvoiceList = signal<InvoiceSummaryViewModel[]>([]);

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Invoices);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const params = AppParameterMap.from(this.router);
    this.entityId.set(params.get(PARAM_ENTITY_ID));

    this.invoicesService.getClientInvoices(this.entityId()).subscribe(invoices => {
      const invoiceList = this.converter.convert(invoices);
      this.clientInvoiceList.set(invoiceList);
      this.clientsideDataService.initialize(invoiceList);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(invoiceList.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  private updateData(): void {
    this.currentPage.set(this.clientsideDataService.generatePage(this.sortField(),
      this.sortOrder() === 1,
      undefined,
      undefined,
      1,
      this.pageSize()));
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.Invoices, this.sortField(), this.sortOrder());
  }

  search(_searchTerm: string): void {
    // Searching is not supported in this view
  }

  page(_targetPage: number): void {
    // Pagination is not supported in this view
  }

  rowClicked(_dataViewModel: DataViewModel): void {
    // Row clicking is not supported in this view
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Entity Name',
        dataProperty: 'customerName',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Invoice Count',
        dataProperty: 'invoiceCount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Currency',
        dataProperty: 'currency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'totalNetBillAmount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Status',
        dataProperty: 'statusIcon',
        cellType: CellType.Icon,
        sortable: true,
        alignment: Alignment.Center,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount Remaining',
        dataProperty: 'totalRemainingAmount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }

}
